import type { FC } from "react";
import { useState } from "react";

import { DEPRECATED_IconButton } from "@ui/components/DEPRECATED_IconButton";
import { ProductSpecificationElement } from "@ui/components/ProductSpecification/components/ProductSpecificationElement";
import { Typography } from "@ui/components/Typography";
import { useTranslations } from "@ui/providers/useTranslations";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { ChevronUpIcon } from "@jti/brand-icons/chevron-up";
import { Card } from "@jti/core-components/components/Card";

import styles from "./ProductSpecification.module.scss";

const SHOW_MORE_THRESHOLD = 4;

export type ProductSpecificationProps = {
  title?: string;
  list?: Record<string, string>;
};

export const ProductSpecification: FC<ProductSpecificationProps> = ({ title, list = {} }) => {
  const { __ } = useTranslations();
  const [isExpanded, setExpanded] = useState(false);

  const items = Object.entries(list).filter(([k, v]) => k && v !== "");
  if (items.length === 0) {
    return null;
  }

  const visibleItems = isExpanded ? items : items.slice(0, SHOW_MORE_THRESHOLD);

  const canToggle = items.length > SHOW_MORE_THRESHOLD;

  const expandToggle = () => setExpanded((show) => !show);

  if (!items.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        {title && (
          <div className={styles.titleWrapper}>
            <Typography className={styles.title} variant="h2">
              {title}
            </Typography>
          </div>
        )}
        <Card>
          <div className={styles.cardElementsWrapper}>
            {visibleItems.map(([key, value], listKey) => (
              <ProductSpecificationElement label={key} value={value} key={listKey} />
            ))}
            {canToggle && (
              <DEPRECATED_IconButton
                icon={!isExpanded ? ChevronDownIcon : ChevronUpIcon}
                className={styles.seeMoreButton}
                iconPosition="end"
                data-testid="product-specification-show-more-toggle-button"
                onClick={expandToggle}
              >
                <span className={styles.seeMoreButtonLabel}>
                  {!isExpanded ? __("product.control.see_more") : __("product.control.see_less")}
                </span>
              </DEPRECATED_IconButton>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
